import type { Book } from "@book-club/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { type BookFormData, bookFormSchema } from "@/features/books/schemas/bookForm.schema";
import type { CreateBookInput } from "@/features/books/types";
import type { TransformedGoogleBook } from "@/features/books/types/googleBooks";
import { checkForDuplicateBook } from "@/features/books/utils/bookDuplicateUtils";
import {
    bookToFormData,
    getDefaultFormValues,
    prepareAuthorFocusData,
    prepareFreeChoiceData,
    prepareSpecificBookData,
} from "@/features/books/utils/bookFormUtils";
import { useDebounce } from "@/shared/hooks/useDebounce";

// Re-export BookFormData for backward compatibility
export type { BookFormData };

type FormReturn = UseFormReturn<BookFormData>;

type UseBookFormReturn = {
    // Watched values
    duplicateBook: Book | null;

    // Form methods
    formMethods: FormReturn;
    handleSubmit: FormReturn["handleSubmit"];

    // Custom handlers
    handleProceedAnyway: () => void;
    handleSelectBook: (book: TransformedGoogleBook) => void;
    handleTypeChange: (type: CreateBookInput["type"]) => void;

    // Computed values
    isFormValid: boolean;
    prepareBookData: (formData: BookFormData) => CreateBookInput | null;
};

/**
 * Shared hook to manage book form state for both add and edit modes using react-hook-form
 *
 * Book types:
 * - "specificBook": A specific book selected for the book club (requires title and author)
 * - "authorFocus": Admin chooses the author. Members can each choose any book by that author (requires author only)
 * - "freeChoice": Members are free to each choose any book they like (no fields required)
 *
 * @param mode - "add" for adding new books, "edit" for editing existing books
 * @param initialData - Optional book data to initialize form (required for edit mode)
 */
export function useBookForm(mode: "add" | "edit", initialData?: Book): UseBookFormReturn {
    const formMethods = useForm<BookFormData>({
        defaultValues: getDefaultFormValues(mode, initialData),
        mode: "onChange",
        resolver: zodResolver(bookFormSchema),
    });

    const { handleSubmit: rhfHandleSubmit, reset, setValue, watch } = formMethods;

    // Watch form values for reactive updates
    const bookType = watch("type");
    const title = watch("title");
    const author = watch("author");
    const isbn = watch("isbn");

    // State for duplicate checking (only in add mode)
    const [duplicateBook, setDuplicateBook] = useState<Book | null>(null);
    const [showProceedAnyway, setShowProceedAnyway] = useState(false);

    // Track if we've initialized the form in edit mode to prevent re-resetting
    const hasInitializedRef = useRef(false);

    // Check for duplicates when form data changes (debounced) - only in add mode
    const debouncedTitle = useDebounce(bookType === "specificBook" ? (title ?? "") : "", 500);
    const debouncedAuthor = useDebounce(
        bookType === "specificBook" || bookType === "authorFocus" ? (author ?? "") : "",
        500
    );
    const debouncedIsbn = useDebounce(bookType === "specificBook" ? (isbn ?? "") : "", 500);

    // Handle duplicate checking logic (only for specificBook type and only in add mode)
    useEffect(() => {
        if (mode === "add" && bookType === "specificBook") {
            checkForDuplicateBook(
                debouncedIsbn || undefined,
                debouncedTitle || undefined,
                debouncedAuthor || undefined,
                showProceedAnyway
            ).then(setDuplicateBook);
        } else {
            setDuplicateBook(null);
        }
    }, [debouncedTitle, debouncedAuthor, debouncedIsbn, showProceedAnyway, bookType, mode]);

    // Reset form when initialData first loads in edit mode (only once)
    useEffect(() => {
        if (mode === "edit" && initialData && !hasInitializedRef.current) {
            reset(bookToFormData(initialData));
            hasInitializedRef.current = true;
        }
    }, [mode, initialData, reset]);

    // Update form data when a book is selected (only in add mode)
    const handleSelectBook = (book: TransformedGoogleBook) => {
        if (mode === "edit") {
            return; // Cannot select from Google Books in edit mode
        }
        const amazonUrl = book.isbn ? `https://www.amazon.co.uk/s?k=${book.isbn}` : "";
        setValue("type", "specificBook", { shouldValidate: true });
        setValue("title", book.title, { shouldValidate: true });
        setValue("author", book.author, { shouldValidate: true });
        setValue("isbn", book.isbn ?? "", { shouldValidate: true });
        setValue("description", book.description ?? "", { shouldValidate: true });
        setValue("coverUrl", book.coverUrl ?? "", { shouldValidate: true });
        setValue("amazonUrl", amazonUrl, { shouldValidate: true });
        setDuplicateBook(null);
        setShowProceedAnyway(false);
    };

    const handleTypeChange = (type: CreateBookInput["type"]) => {
        if (mode === "edit") {
            return; // Cannot change type in edit mode
        }
        // Reset form to default values for the new type
        if (type === "specificBook") {
            reset({
                amazonUrl: "",
                author: "",
                coverUrl: "",
                description: "",
                isbn: "",
                title: "",
                type: "specificBook",
            });
        } else if (type === "authorFocus") {
            reset({
                amazonUrl: "",
                author: "",
                type: "authorFocus",
            });
        } else {
            reset({
                type: "freeChoice",
            });
        }
        setDuplicateBook(null);
        setShowProceedAnyway(false);
    };

    const handleProceedAnyway = () => {
        if (mode === "edit") {
            return; // Cannot proceed anyway in edit mode (no duplicates to check)
        }
        setShowProceedAnyway(true);
        setDuplicateBook(null);
    };

    // Compute form validity manually based on current values
    // This is more reliable than relying on react-hook-form's isValid which may not update synchronously
    const computeIsFormValid = (): boolean => {
        if (!bookType) {
            return false; // Type must be selected
        }
        if (bookType === "specificBook") {
            return (title?.trim().length ?? 0) > 0 && (author?.trim().length ?? 0) > 0;
        }
        if (bookType === "authorFocus") {
            return (author?.trim().length ?? 0) > 0;
        }
        // freeChoice has no requirements
        return true;
    };

    const isFormValidComputed = computeIsFormValid();

    // Prepare book data for submission
    const prepareBookData = (data: BookFormData): CreateBookInput | null => {
        if (!data.type) {
            return null;
        }

        switch (data.type) {
            case "specificBook":
                return prepareSpecificBookData(data);
            case "authorFocus":
                return prepareAuthorFocusData(data);
            case "freeChoice":
                return prepareFreeChoiceData();
            default:
                return null;
        }
    };

    return {
        duplicateBook,
        formMethods,
        handleProceedAnyway,
        handleSelectBook,
        handleSubmit: rhfHandleSubmit,
        handleTypeChange,
        isFormValid: isFormValidComputed,
        prepareBookData,
    };
}
